import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { api } from '../api/client';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { formatAuditValue, formatDateTime } from '../format';

export function AuditLog() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['audit', search],
    queryFn: () => api.listAuditLog(search || undefined),
    placeholderData: keepPreviousData,
  });

  const applySearch = () => setSearch(searchInput.trim());

  return (
    <div className="page">
      <h1 className="page-title">Audit log</h1>
      <p className="page-subtitle">Every change across every complaint, last 7 days.</p>

      <div className="toolbar">
        <Field label="Search" htmlFor="audit_search">
          <input
            id="audit_search"
            value={searchInput}
            placeholder="Complaint no., application no., loan no., or complainant name…"
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          />
        </Field>
        <button type="button" className="btn" onClick={applySearch}>
          <Search size={15} aria-hidden /> Search
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <Loading />
        ) : isError ? (
          <div className="notice error">{(error as Error).message}</div>
        ) : !data || data.items.length === 0 ? (
          <p className="faint" style={{ fontSize: 12 }}>No audit entries in the last 7 days.</p>
        ) : (
          <>
            {data.items.map((entry) => (
              <div key={entry.id} className="log-row">
                <span className="log-time">{formatDateTime(entry.created_at)}</span>
                <span className="log-actor">{entry.actor_email}</span>
                {entry.complaint_id && (
                  <span
                    className="reference"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/complaints/${entry.complaint_id}`)}
                  >
                    {entry.reference}
                  </span>
                )}
                <span className="faint" style={{ fontSize: 11 }}>
                  {[entry.first_name, entry.last_name].filter(Boolean).join(' ')}
                </span>
                <span className="faint" style={{ fontSize: 11 }}>
                  {[entry.application_number, entry.loan_number].filter(Boolean).join(' / ')}
                </span>
                <span style={{ flex: 1 }}>
                  {entry.action}
                  {entry.field_changed ? ` — ${entry.field_changed}` : ''}
                  {entry.new_value ? ` → ${formatAuditValue(entry.field_changed, entry.new_value).slice(0, 50)}` : ''}
                </span>
              </div>
            ))}
            <p className="faint" style={{ fontSize: 11, marginTop: 10 }}>
              {data.items.length} entr{data.items.length !== 1 ? 'ies' : 'y'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
